import { type FC, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { FolderKey, Mail, Users, UsersRound } from "lucide-react";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";
import PageContent from "@/pages/PageContent";
import { Tabs, TabsList, TabsTrigger } from "@capacit-com/lara-law-shared-ui";
import AppRoutes from "@/constants/routes/AppRoutes";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import UserManagementProvider from "@/providers/user-management/UserManagementProvider";
import { useUserManagementContext } from "@/providers/user-management/UserManagementContext";
import UserManagementPrimaryAction from "./components/UserManagementPrimaryAction";
import { UserManagementTabs } from "./utils/userManagementTabs";

const UserManagementContent: FC = () => {
  /**** HOOKS ****/
  const location = useLocation();
  const navigate = useNavigate();
  const { setBulkMode, selectedUsers, tenant, isLoadingTenant } =
    useUserManagementContext();

  /**** STATE & QUERIES ****/

  // Determine current tab from the route
  const getCurrentTab = (): UserManagementTabs => {
    const path = location.pathname;
    switch (path) {
      case AppRoutes.settingsUserManagementUsers:
        return UserManagementTabs.USERS;
      case AppRoutes.settingsUserManagementInvitedUsers:
        return UserManagementTabs.INVITED_USERS;
      case AppRoutes.settingsUserManagementGroups:
        return UserManagementTabs.GROUPS;
      case AppRoutes.settingsUserManagementOrphanedWorkspaces:
        return UserManagementTabs.ORPHANED_WORKSPACES;
      default:
        return UserManagementTabs.USERS;
    }
  };

  const [selectedTab, setSelectedTab] =
    useState<UserManagementTabs>(getCurrentTab());

  const handleTabChange = (tab: UserManagementTabs) => {
    // Bulk mode belongs to the tab you turned it on in, so drop it before leaving.
    setBulkMode(false);

    switch (tab) {
      case UserManagementTabs.USERS:
        navigate(AppRoutes.settingsUserManagementUsers);
        break;
      case UserManagementTabs.INVITED_USERS:
        navigate(AppRoutes.settingsUserManagementInvitedUsers);
        break;
      case UserManagementTabs.GROUPS:
        navigate(AppRoutes.settingsUserManagementGroups);
        break;
      case UserManagementTabs.ORPHANED_WORKSPACES:
        navigate(AppRoutes.settingsUserManagementOrphanedWorkspaces);
        break;
      default:
        navigate(AppRoutes.settingsUserManagementUsers);
        break;
    }
  };

  useEffect(() => {
    // Redirect base route to users tab
    if (location.pathname === AppRoutes.settingsUserManagement) {
      navigate(AppRoutes.settingsUserManagementUsers, { replace: true });
      return;
    }
    setSelectedTab(getCurrentTab());
    // Covers browser back/forward, which never goes through handleTabChange.
    setBulkMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navigate]);

  return (
    <PageContent title="Brugerstyring">
      <div className="flex flex-col h-full w-full space-y-4 p-4 xl:px-8 min-h-0">
        {/*
          The header reserves the height of the tallest description (three lines plus its
          padding) so the tabs, search row and table below sit at the same height on every
          sub-tab instead of jumping as the description grows or shrinks.
        */}
        <div className="flex justify-between items-start gap-4 min-h-23">
          <p className="text-sm p-4 bg-accent text-accent-foreground rounded-md w-fit">
            {selectedTab === UserManagementTabs.USERS && (
              <>
                Overblik over hvilke brugere som er i{" "}
                <span className={cn(isLoadingTenant && "animate-pulse")}>
                  {isLoadingTenant ? "organisationen" : tenant?.name}
                </span>
                . Her har du mulighed for at tildele dem roller.
              </>
            )}
            {selectedTab === UserManagementTabs.INVITED_USERS && (
              <>
                Overblik over hvilke brugere som er inviteret til{" "}
                <span className={cn(isLoadingTenant && "animate-pulse")}>
                  {isLoadingTenant ? "organisationen" : tenant?.name}
                </span>
                . Her har du mulighed for at invitere nye brugere.
              </>
            )}
            {selectedTab === UserManagementTabs.GROUPS && (
              <>
                Opret og administrer grupper. Man kan bl.a. sende beskeder som
                notifikationer ud til brugere i en gruppe, eller tildele
                abonnementer.
              </>
            )}
            {selectedTab === UserManagementTabs.ORPHANED_WORKSPACES && (
              <>
                Overblik over workspaces uden ejer. Disse workspaces kan
                tildeles en ny ejer.
              </>
            )}
          </p>
          <UserManagementPrimaryAction tab={selectedTab} />
        </div>

        <Tabs
          value={selectedTab}
          className="flex flex-col gap-2 workspace-tabs"
        >
          <TabsList className="tabs-list">
            <TabsTrigger
              value={UserManagementTabs.USERS}
              onClick={() => handleTabChange(UserManagementTabs.USERS)}
              className="tab-trigger"
            >
              <Users className="w-4 h-4 mr-1" />
              Brugere
            </TabsTrigger>
            <PermissionGate allOf={[Permission.ReadOrganizationInvitation]}>
              <TabsTrigger
                value={UserManagementTabs.INVITED_USERS}
                onClick={() =>
                  handleTabChange(UserManagementTabs.INVITED_USERS)
                }
                className="tab-trigger"
              >
                <Mail className="w-4 h-4 mr-1" />
                Inviterede brugere
              </TabsTrigger>
            </PermissionGate>
            <PermissionGate
              oneOf={[
                Permission.CreateFeatureGroups,
                Permission.UpdateFeatureGroups,
              ]}
            >
              <TabsTrigger
                value={UserManagementTabs.GROUPS}
                onClick={() => handleTabChange(UserManagementTabs.GROUPS)}
                className="tab-trigger"
              >
                <UsersRound className="w-4 h-4 mr-1" />
                Grupper
              </TabsTrigger>
            </PermissionGate>
            <PermissionGate allOf={[Permission.ManageOrphanedWorkspaces]}>
              <TabsTrigger
                value={UserManagementTabs.ORPHANED_WORKSPACES}
                onClick={() =>
                  handleTabChange(UserManagementTabs.ORPHANED_WORKSPACES)
                }
                className="tab-trigger"
              >
                <FolderKey className="w-4 h-4 mr-1" />
                Forældreløse workspaces
              </TabsTrigger>
            </PermissionGate>
          </TabsList>
        </Tabs>
        <div className="flex-1 min-h-0 flex flex-col">
          <Outlet context={{ selectedUsers }} />
        </div>
      </div>
    </PageContent>
  );
};

const UserManagement: FC = () => {
  return (
    <UserManagementProvider>
      <UserManagementContent />
    </UserManagementProvider>
  );
};

export default UserManagement;
